import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { vendorApi, productApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Mail, Phone, MapPin, X } from 'lucide-react';

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    abn: '',
    emails: [''],
    address: '',
    phone: '',
    notes: '',
  });

  const fetchData = async () => {
    try {
      const [vendorsRes, productsRes] = await Promise.all([
        vendorApi.getAll(),
        productApi.getAll()
      ]);
      setVendors(vendorsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Data fetch runs once on mount
  }, []);

  const getVendorProductCount = (vendorId) => {
    return products.filter(p => p.vendor_id === vendorId).length;
  };

  const openModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name,
        abn: vendor.abn,
        emails: vendor.emails?.length > 0 ? vendor.emails : [''],
        address: vendor.address || '',
        phone: vendor.phone || '',
        notes: vendor.notes || '',
      });
    } else {
      setEditingVendor(null);
      setFormData({
        name: '',
        abn: '',
        emails: [''],
        address: '',
        phone: '',
        notes: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleAddEmail = () => {
    setFormData({ ...formData, emails: [...formData.emails, ''] });
  };

  const handleRemoveEmail = (index) => {
    const newEmails = formData.emails.filter((_, i) => i !== index);
    setFormData({ ...formData, emails: newEmails.length > 0 ? newEmails : [''] });
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...formData.emails];
    newEmails[index] = value;
    setFormData({ ...formData, emails: newEmails });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanedEmails = formData.emails.filter(email => email.trim() !== '');
    const submitData = { ...formData, emails: cleanedEmails };

    try {
      if (editingVendor) {
        await vendorApi.update(editingVendor.id, submitData);
        toast.success('Vendor updated successfully');
      } else {
        await vendorApi.create(submitData);
        toast.success('Vendor created successfully');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving vendor:', error);
      toast.error('Failed to save vendor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await vendorApi.delete(id);
      toast.success('Vendor deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error('Failed to delete vendor');
    }
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.abn.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Vendors</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your suppliers and their contact information
          </p>
        </div>
        <Button className="rounded-sm" onClick={() => openModal()} data-testid="btn-add-vendor">
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or ABN..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="vendor-search"
        />
      </div>

      {/* Vendors Grid */}
      {filteredVendors.length === 0 ? (
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No vendors found</p>
            <Button variant="link" onClick={() => openModal()} className="mt-2">
              Add your first vendor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVendors.map((vendor) => (
            <Card key={vendor.id} className="border border-border shadow-none rounded-sm" data-testid={`vendor-card-${vendor.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base font-heading">{vendor.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openModal(vendor)}
                      data-testid={`btn-edit-vendor-${vendor.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDelete(vendor.id)}
                      data-testid={`btn-delete-vendor-${vendor.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono-data text-xs rounded-none">
                    ABN: {vendor.abn}
                  </Badge>
                  <Badge variant="outline" className="text-xs rounded-none">
                    {getVendorProductCount(vendor.id)} products
                  </Badge>
                </div>
                {vendor.emails?.length > 0 && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="space-y-0.5">
                      {vendor.emails.map((email) => (
                        <div key={email} className="text-xs">{email}</div>
                      ))}
                    </div>
                  </div>
                )}
                {vendor.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    <span className="font-mono-data text-xs">{vendor.phone}</span>
                  </div>
                )}
                {vendor.address && (
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="text-xs">{vendor.address}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg" data-testid="vendor-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
            <DialogDescription>
              {editingVendor ? 'Update vendor details below.' : 'Fill in the vendor details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Vendor Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-sm mt-1.5"
                  required
                  data-testid="input-vendor-name"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="abn">ABN *</Label>
                <Input
                  id="abn"
                  value={formData.abn}
                  onChange={(e) => setFormData({ ...formData, abn: e.target.value })}
                  className="rounded-sm mt-1.5 font-mono-data"
                  placeholder="XX XXX XXX XXX"
                  required
                  data-testid="input-vendor-abn"
                />
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Email Addresses</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddEmail}
                    className="h-7 text-xs"
                    data-testid="btn-add-email"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Email
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.emails.map((email, index) => (
                    <div key={`email-input-${index}`} className="flex gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => handleEmailChange(index, e.target.value)}
                        placeholder="email@example.com"
                        className="rounded-sm"
                        data-testid={`input-vendor-email-${index}`}
                      />
                      {formData.emails.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveEmail(index)}
                          className="shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-sm mt-1.5"
                  data-testid="input-vendor-phone"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="rounded-sm mt-1.5"
                  data-testid="input-vendor-address"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="rounded-sm mt-1.5"
                  rows={3}
                  data-testid="input-vendor-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="rounded-sm" data-testid="btn-save-vendor">
                {editingVendor ? 'Update Vendor' : 'Add Vendor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
